<template>
  <v-dialog v-model="visible" width="80%" :fullscreen="$vuetify.breakpoint.smAndDown" persistent scrollable>
    <v-card :loading="loading" :height="result.length > 0 ? 700 : ''" class="rounded-xl">
      <v-card-title>
        <v-chip class="mr-2" color="green" dark>Neu</v-chip>
        {{ rechnungen.length }} Rechnung versenden
        <v-spacer></v-spacer>
        <v-btn icon @click="close()"><v-icon>close</v-icon></v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="my-4">
        <v-row v-if="result.length < versendeRechnung.gesamt || versendeRechnung.gesamt === 0">
          <v-col cols="6">
            <v-alert type="warning" dismissible>
              Achtung! Diese Funktion ist noch neu und im Test. Bitte nur in Absprache mit Benedikt verwenden.
              <!-- Achtung, diese Funktion wurde komplett überarbeitet. Daher bitte beim ersten Nutzen besonders gut durchlesen! -->
            </v-alert>
            <p>Durch diese Funktion wird für jede Rechnung</p>
            <li class="pt-1">ein PDF generiert, welches auf dem Server gespeichert ist.</li>
            <li class="pt-1">eine Mail an den Rechnungsempfänger geschickt.</li>
            <li class="pt-1">die Rechnung als "offen" bzw "gezahlt" (bei SEPA) markiert.</li>
            <li class="pt-1">eine SEPA Datei wird generiert und heruntergeladen.</li>
            <p class="mt-3">
              Der Versand kann mehrere Minuten dauern. <br />
              Das Fenster darf nicht geschlossen werden.
            </p>

            <div class="text-right">
              <v-btn
                rounded
                class="primary"
                :disabled="loading"
                :loading="loading"
                @click="rechnungVersenden(rechnungen)"
              >
                <template v-slot:loader>
                  <span class="ml-2">{{ $filters.date(timer, "mmss") }}</span>
                </template>
                Rechnungen versenden
              </v-btn>
            </div>
            <div v-if="result.length !== versendeRechnung.gesamt && (result.length > 0 || loading)" class="mt-10">
              <v-progress-linear
                v-if="createPDF.gesamt > 0"
                :value="(createPDF.erledigt / createPDF.gesamt) * 100"
                color="success"
                class="rounded mb-4"
                height="24"
                stream
                buffer-value="0"
              >
                Rechnungs-PDF wird generiert: {{ createPDF.erledigt }} von {{ createPDF.gesamt }}
              </v-progress-linear>
              <v-progress-linear
                :value="(versendeRechnung.erledigt / versendeRechnung.gesamt) * 100"
                color="success"
                class="rounded"
                height="24"
                stream
                buffer-value="0"
              >
                {{ versendeRechnung.erledigt }} von {{ versendeRechnung.gesamt }} gesendet
              </v-progress-linear>
            </div>
          </v-col>

          <v-col cols="6">
            <v-card outlined class="rounded-xl">
              <v-card-title>Diese Rechnungen werden gesendet:</v-card-title>
              <v-simple-table dense height="400px">
                <tbody>
                  <tr v-for="(item, index) in rechnungen" :key="index">
                    <td>{{ item.rechnungsnummer }}</td>
                    <td>{{ $filters.user(item.bbrID, bbrbbr) }}</td>
                    <td class="text-center">{{ item.rechnung_art }}</td>
                    <td class="text-right">{{ item.summe | money }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card>
          </v-col>
        </v-row>
        <v-row v-if="result.length > 0">
          <v-col cols="12" class="text-right">
            <v-chip label>Dauer: {{ $filters.date(timer, "mmss") }}</v-chip>
          </v-col>
          <v-col cols="6">
            <v-card outlined class="pa-4 pb-0">
              <div class="d-flex align-center">
                <v-progress-circular
                  :rotate="-90"
                  :value="(result.filter((item) => !item.error).length / versendeRechnung.gesamt) * 100"
                  color="success"
                  size="64"
                  width="8"
                >
                  <v-icon color="success" size="36">check_circle</v-icon>
                </v-progress-circular>
                <strong class="ml-4 success--text"
                  >{{ result.filter((item) => !item.error).length }} Rechnungen wurden erfolgreich gesendet.</strong
                >
              </div>
              <v-card-actions class="d-flex justify-center py-0">
                <v-btn text small @click="showLogFilter = false"><v-icon>keyboard_arrow_down</v-icon>Anzeigen</v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
          <v-col cols="6">
            <v-card outlined class="pa-4 pb-0">
              <div class="d-flex align-center">
                <v-progress-circular
                  :rotate="-90"
                  :value="(result.filter((item) => item.error).length / versendeRechnung.gesamt) * 100"
                  color="error"
                  size="64"
                  width="8"
                >
                  <v-icon color="error" size="36">error</v-icon>
                </v-progress-circular>
                <strong class="ml-4 error--text"
                  >{{ result.filter((item) => item.error).length }} Rechnungen konnten nicht gesendet werden.</strong
                >
              </div>
              <v-card-actions class="d-flex justify-center py-0">
                <v-btn text small @click="showLogFilter = true"><v-icon>keyboard_arrow_down</v-icon>Anzeigen</v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
          <v-col cols="12">
            <v-card outlined>
              <v-data-table
                :headers="headers"
                :items="result.filter((item) => item.error === showLogFilter)"
                :expanded.sync="expanded"
                single-expand
                dense
                :items-per-page="-1"
                height="370"
                item-key="rechnung"
                show-expand
                hide-default-footer
              >
                <template v-slot:item.error="{ item }">
                  <td width="20px">
                    <v-icon :color="item.error ? 'error' : 'success'">
                      {{ item.error ? "warning" : "check" }}
                    </v-icon>
                  </td>
                </template>
                <template v-slot:item.rechnung="{ item }">
                  <td width="100%" class="d-flex align-center justify-space-between">
                    {{ versendeRechnung.rechnungen.find((a) => a.id === item.rechnung).rechnungsnummer }}
                    <span v-if="item.error"
                      ><v-btn outlined small @click="erneutVersuchen(item)">Erneut senden</v-btn></span
                    >
                  </td>
                </template>
                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="2" class="py-2">
                    <v-list dense>
                      <v-list-item v-for="(log, j) in item.log" :key="j">
                        <v-list-item-icon>
                          <v-icon :color="log.error ? 'error' : 'success'">
                            {{ log.error ? "warning" : "check" }}
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>{{ log.message }}</v-list-item-title>
                          <v-list-item-subtitle v-if="log.error">Funktion: {{ log.function }}</v-list-item-subtitle>
                          <v-list-item-subtitle v-if="log.error">data: {{ log.data }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </td>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { generatePDFRechnungToLink } from "./../../funktionen/generatePDFRechnungToLink.js";
  export default {
    props: ["visible", "rechnungen"],
    data() {
      return {
        timer: 0,
        intervalId: null,
        startTime: null,
        expanded: [],
        loading: false,
        createPDF: { gesamt: 0, erledigt: 0 },
        versendeRechnung: { gesamt: 0, erledigt: 0, rechnungen: [] },
        result: [],
        showLogFilter: true,
        headers: [
          { text: "", value: "error" },
          { text: "Rechnungsnummer", value: "rechnung" },
          { text: "", value: "data-table-expand" },
        ],
      };
    },
    computed: {
      currentBbr() {
        return this.$getCurrentBbr();
      },
      bbrbbr() {
        return this.$store.state.bbrbbr.bbrbbr;
      },
    },
    methods: {
      async erneutVersuchen(rechnung) {
        this.result.splice(this.result.indexOf(rechnung), 1);
        await this.rechnungVersenden([this.rechnungen.find((item) => item.id === rechnung.rechnung)]);
      },
      async rechnungVersenden(rechnungen) {
        try {
          this.startTimer();
          this.loading = true;
          this.versendeRechnung = {
            gesamt: rechnungen.length,
            erledigt: 0,
            rechnungen: [...rechnungen],
          };
          console.log(this.versendeRechnung);
          const groupedPromises = [];
          const batchSize = 50;
          for (let i = 0; i < rechnungen.length; i += batchSize) {
            const batch = rechnungen.slice(i, i + batchSize);
            const promises = batch.map(async (rechnung) => {
              try {
                const kasse = this.$store.getters["einstellungen/einstellung"]("kassen")[rechnung.kasse];
                const year = rechnung.rechnungsnummer.split("-")[0];
                const storagePath = `rechnungen/${this.$vars.verbindung}/${year}/${rechnung.rechnungsnummer}`;
                const file = await this.$firebase.functions().httpsCallable("existsPDF")({
                  path: storagePath,
                  rechnung,
                  kasse,
                  verein: this.$vars.verbindung,
                });
                if (!file.data) {
                  this.createPDF.gesamt++;
                  const bbr = this.$store.getters["bbrbbr/bbr"](rechnung.bbrID);
                  const settings = this.$store.getters["einstellungen/einstellung"]("allgemein");
                  await generatePDFRechnungToLink(rechnung, kasse, bbr, settings, this.$vars.verbindung);
                  this.createPDF.erledigt++;
                }

                const result = await this.$firebase.functions().httpsCallable("sendRechnungen")({
                  rechnungen: [rechnung],
                  verein: this.$vars.verbindung,
                });
                // Rechnungsstatus updaten
                if (!result.data[0].error) {
                  let rechnung = this.versendeRechnung.rechnungen.find((item) => item.id === result.data[0].rechnung);
                  if (rechnung.rechnung_art === "SEPA") {
                    rechnung.status = "Gezahlt";
                  }
                  if (rechnung.rechnung_art === "Überweisung") {
                    rechnung.status = "Offen";
                  }
                  this.$store.dispatch("rechnungen/updateRechnungData", rechnung);
                }
                this.versendeRechnung.erledigt++;
                this.result.push(result.data[0]);
                return result.data;
              } catch (error) {
                console.error("Fehler beim Versenden der Rechnung:", error);
                return { error: true, message: error.message };
              }
            });
            groupedPromises.push(promises);
          }

          for (const promises of groupedPromises) {
            await Promise.all(promises);
          }
          console.log(rechnungen[0]);
          console.log(rechnungen[0].rechnung_art === "SEPA");
          console.log(rechnungen.filter((rechnung) => rechnung.rechnung_art === "SEPA"));
          if (rechnungen[0].rechnung_art === "SEPA") {
            this.sepaGenerieren(rechnungen.filter((rechnung) => rechnung.rechnung_art === "SEPA"));
          }

          this.loading = false;
          this.stopTimer();
        } catch (error) {
          console.error("Fehler beim Versenden der Rechnungen:", error);
          this.loading = false;
        }
      },
      startTimer() {
        if (this.intervalId) return;
        this.intervalId = setInterval(() => {
          this.timer = new Date() - this.startTime;
        }, 1000);
        this.startTime = this.startTime > 0 ? this.startTime : new Date();
      },
      stopTimer() {
        clearInterval(this.intervalId);
        this.intervalId = null;
      },
      sepaGenerieren(rechnungen) {
        console.log("sepaGenerieren");
        this.loading = true;
        let anzahlRechnungen = this.rechnungen.length;
        let summeRechnungen = 0;
        rechnungen.forEach((rechnung) => {
          summeRechnungen = summeRechnungen + parseFloat(parseFloat(rechnung.summe).toFixed(2));
        });
        summeRechnungen = summeRechnungen.toFixed(2);
        let kassenInfo = this.$store.getters["einstellungen/einstellung"]("kassen")[rechnungen[0].kasse];
        kassenInfo.IBAN = kassenInfo.IBAN.replace(/ /g, "");
        let nachrichtenID = "ID-1863-" + new Date().getTime();
        let nachrichtenErstellung = new Date().toISOString().substr(0, 24);
        let rechnungZahlbarBis = rechnungen[0].rechnung_zahlbar_bis;
        let xmltext;
        try {
          xmltext =
            '<?xml version="1.0" encoding="UTF-8"?>\n' +
            '<Document xmlns="urn:iso:std:iso:20022:tech:xsd:pain.008.001.02" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="urn:iso:std:iso:20022:tech:xsd:pain.008.001.02 pain.008.001.02.xsd"><CstmrDrctDbtInitn>\n' +
            "\t<GrpHdr>\n" +
            "\t\t<MsgId>" +
            nachrichtenID +
            "</MsgId>\n" +
            "\t\t<CreDtTm>" +
            nachrichtenErstellung +
            "</CreDtTm>\n" +
            "\t\t<NbOfTxs>" +
            anzahlRechnungen +
            "</NbOfTxs>\n" +
            "\t\t<CtrlSum>" +
            summeRechnungen +
            "</CtrlSum>\n" +
            "\t\t<InitgPty>\n" +
            "\t\t\t<Nm>" +
            kassenInfo.kontoname +
            "</Nm>\n" +
            "\t\t</InitgPty>\n" +
            "\t</GrpHdr>\n" +
            "\t<PmtInf>\n" +
            "\t\t<PmtInfId>" +
            nachrichtenID +
            "</PmtInfId>\n" +
            "\t\t<PmtMtd>DD</PmtMtd>\n" +
            "\t\t<BtchBookg>true</BtchBookg>\n" +
            "\t\t<NbOfTxs>" +
            anzahlRechnungen +
            "</NbOfTxs>\n" +
            "\t\t<CtrlSum>" +
            summeRechnungen +
            "</CtrlSum>\n" +
            "\t\t<PmtTpInf>\n" +
            "\t\t\t<SvcLvl>\n" +
            "\t\t\t\t<Cd>SEPA</Cd>\n" +
            "\t\t\t</SvcLvl>\n" +
            "\t\t\t<LclInstrm>\n" +
            "\t\t\t\t<Cd>CORE</Cd>\n" +
            "\t\t\t</LclInstrm>\n" +
            "\t\t\t<SeqTp>RCUR</SeqTp>\n" +
            "\t\t</PmtTpInf>\n" +
            "\t\t<ReqdColltnDt>" +
            rechnungZahlbarBis +
            "</ReqdColltnDt>\n" +
            "\t\t<Cdtr>\n" +
            "\t\t\t<Nm>" +
            kassenInfo.kontoname +
            "</Nm>\n" +
            "\t\t</Cdtr>\n" +
            "\t\t<CdtrAcct>\n" +
            "\t\t\t<Id>\n" +
            "\t\t\t\t<IBAN>" +
            kassenInfo.IBAN +
            "</IBAN>\n" +
            "\t\t\t</Id>\n" +
            "\t\t</CdtrAcct>\n" +
            "\t\t<CdtrAgt>\n" +
            "\t\t\t<FinInstnId>\n" +
            "\t\t\t\t<BIC>" +
            kassenInfo.BIC +
            "</BIC>\n" +
            "\t\t\t</FinInstnId>\n" +
            "\t\t</CdtrAgt>\n" +
            "\t\t<ChrgBr>SLEV</ChrgBr>\n" +
            "\t\t<CdtrSchmeId>\n" +
            "\t\t\t<Id>\n" +
            "\t\t\t\t<PrvtId>\n" +
            "\t\t\t\t\t<Othr>\n" +
            "\t\t\t\t\t\t<Id>" +
            kassenInfo.glaubigerID +
            "</Id>\n" +
            "\t\t\t\t\t\t<SchmeNm>\n" +
            "\t\t\t\t\t\t\t<Prtry>SEPA</Prtry>\n" +
            "\t\t\t\t\t\t</SchmeNm>\n" +
            "\t\t\t\t\t</Othr>\n" +
            "\t\t\t\t</PrvtId>\n" +
            "\t\t\t</Id>\n" +
            "\t\t</CdtrSchmeId>";

          rechnungen.forEach((rechnung) => {
            let bundesbruder = this.$store.getters["bbrbbr/bbr"](rechnung.bbrID);
            let betrag = parseFloat(rechnung.summe).toFixed(2);
            let EndToEndId = "RBO" + rechnung.rechnungsnummer;
            let MndtId = bundesbruder.mandatsreferenz.replace(/[^A-Z0-9]/g, "");
            let DtOfSgntr = bundesbruder.mandatUnterschriftsdatum;
            let AmdmntInd = "false";
            let BIC = bundesbruder.bic;
            let name = bundesbruder.kontoinhaber;
            let IBAN = bundesbruder.iban.replace(/ /g, "");
            let betreff = (rechnung.rechnung_titel + " " + rechnung.rechnungsnummer).replace(/[^\x00-\x7F]/g, "");
            rechnung.gezahlt_datum = new Date(rechnung.rechnung_zahlbar_bis);
            xmltext =
              xmltext +
              "\n" +
              "\t<DrctDbtTxInf>\n" +
              "\t\t<PmtId>\n" +
              "\t\t\t<EndToEndId>" +
              EndToEndId +
              "</EndToEndId>\n" +
              "\t\t</PmtId>\n" +
              '\t\t<InstdAmt Ccy="EUR">' +
              betrag +
              "</InstdAmt>\n" +
              "\t\t<DrctDbtTx>\n" +
              "\t\t\t<MndtRltdInf>\n" +
              "\t\t\t\t<MndtId>" +
              MndtId +
              "</MndtId>\n" +
              "\t\t\t\t<DtOfSgntr>" +
              DtOfSgntr +
              "</DtOfSgntr>\n" +
              "\t\t\t\t<AmdmntInd>" +
              AmdmntInd +
              "</AmdmntInd>\n" +
              "\t\t\t</MndtRltdInf>\n" +
              "\t\t</DrctDbtTx>\n" +
              "\t\t<DbtrAgt>\n" +
              "\t\t\t<FinInstnId>\n" +
              "\t\t\t\t<BIC>" +
              BIC +
              "</BIC>\n" +
              "\t\t\t</FinInstnId>\n" +
              "\t\t</DbtrAgt>\n" +
              "\t\t<Dbtr>\n" +
              "\t\t\t<Nm>" +
              name +
              "</Nm>\n" +
              "\t\t</Dbtr>\n" +
              "\t\t<DbtrAcct>\n" +
              "\t\t\t<Id>\n" +
              "\t\t\t\t<IBAN>" +
              IBAN +
              "</IBAN>\n" +
              "\t\t\t</Id>\n" +
              "\t\t</DbtrAcct>\n" +
              "\t\t<RmtInf>\n" +
              "\t\t\t<Ustrd>" +
              betreff +
              "</Ustrd>\n" +
              "\t\t</RmtInf>\n" +
              "\t</DrctDbtTxInf>";
          });

          xmltext = xmltext + "\n" + "\t</PmtInf>\n" + "</CstmrDrctDbtInitn>\n" + "</Document>";
        } catch (err) {
          console.error(err);
          this.$store.state.nachricht.typ = "error";
          this.$store.state.nachricht.nachricht = "Die Erstellung der SEPA-Datei hat nicht geklappt.";
          this.$store.state.nachricht.error = err;
          this.$store.state.nachricht.show = true;
        }
        let filename =
          new Date().toISOString().substr(0, 10) +
          "-" +
          new Date().toISOString().substr(11, 8) +
          "_sepa_" +
          rechnungen[0].kasse +
          ".xml";
        // Generieren und Download der Datei
        let pom = document.createElement("a");
        pom = document.createElement("a");
        let bb = new Blob([xmltext], { type: "text/plain" });
        pom.setAttribute("href", window.URL.createObjectURL(bb));
        pom.setAttribute("download", filename);
        pom.dataset.downloadurl = ["text/plain", pom.download, pom.href].join(":");
        pom.draggable = true;
        pom.classList.add("dragout");
        pom.click();
        console.log(summeRechnungen);
        this.$store.dispatch("sepa/createEinSepa", {
          date: new Date(),
          xml: xmltext,
          filename: filename,
          summe: summeRechnungen,
          anzahl: anzahlRechnungen,
          kasse: kassenInfo.kontoname,
        });
        this.loading = false;
        this.progressSendSepa = 0;
        this.stepperSendRechnungen = 2;
      },
      close() {
        this.$emit("close", false);
      },
    },
    beforeDestroy() {
      clearInterval(this.intervalId);
    },
  };
</script>

<style></style>



// WEBPACK FOOTER //
// dialogRechnungenVersenden.vue?73a8523e